import React from 'react'
import { inject, observer } from 'mobx-react'
import { compose, withState, lifecycle } from 'recompose'
import { startProCheckout } from '../../proCheckout'

const UpgradeToProCardView = ({
  isLimitExceeded,
  limitMessage,
  isExpanded,
  isVisible,
  setIsVisible,
  uiStore
}) => {
  if (!isLimitExceeded || !isVisible) return null

  const cardStyle = {
    position: 'fixed',
    top: '24px',
    right: isExpanded ? 'min(calc(29vw + 24px), 579px)' : '24px',
    width: '258px',
    borderRadius: '12px',
    padding: '16px',
    backgroundColor: '#FFFFFF',
    boxShadow: '0px 20px 25px -5px rgba(0, 0, 0, 0.1), 0px 10px 10px -5px rgba(0, 0, 0, 0.04)',
    zIndex: 10000,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    border: '1px solid rgba(0, 0, 0, 0.05)',
    transition: 'right 0.3s ease-in-out',
    boxSizing: 'border-box'
  }

  const titleStyle = {
    fontFamily: 'Inter, system-ui, sans-serif',
    fontWeight: 700,
    fontSize: '16px',
    color: '#111827',
    margin: '0 0 8px 0'
  }

  const descriptionStyle = {
    fontFamily: 'Inter, system-ui, sans-serif',
    fontWeight: 400,
    fontSize: '13px',
    lineHeight: '1.5',
    color: '#4B5563',
    margin: '0 0 16px 0'
  }

  const buttonStyle = {
    width: '100%',
    height: '40px',
    borderRadius: '8px',
    border: 'none',
    backgroundColor: '#10B981',
    color: '#FFFFFF',
    fontFamily: 'Inter, system-ui, sans-serif',
    fontWeight: 600,
    fontSize: '14px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'background-color 0.2s',
    margin: 0
  }

  const closeButtonStyle = {
    position: 'absolute',
    top: '12px',
    right: '12px',
    background: 'transparent',
    border: 'none',
    cursor: 'pointer',
    padding: '4px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#9CA3AF',
    transition: 'color 0.2s',
    width: '24px',
    height: '24px',
    margin: 0,
    zIndex: 1
  }

  const handleUpgradeClick = async () => {
    await startProCheckout({ uiStore })
  }

  return (
    <div style={cardStyle} className='upgrade-pro-card'>
      <button
        style={closeButtonStyle}
        onClick={() => setIsVisible(false)}
        onMouseOver={(e) => { e.currentTarget.style.color = '#4B5563' }}
        onMouseOut={(e) => { e.currentTarget.style.color = '#9CA3AF' }}
        aria-label='Close'
      >
        <svg width='20' height='20' viewBox='0 0 20 20' fill='none' xmlns='http://www.w3.org/2000/svg'>
          <path d='M15 5L5 15M5 5L15 15' stroke='currentColor' strokeWidth='1.5' strokeLinecap='round' strokeLinejoin='round' />
        </svg>
      </button>
      <div style={{ width: '100%' }}>
        <h3 style={titleStyle}>You’ve reached the limit</h3>
        <p style={descriptionStyle}>
          {(() => {
            const rawMsg = limitMessage || 'Upgrade to Pro to unlock unlimited POI searches, saved markers, data exports, and more.'
            // Remove redundant title if present
            const msg = rawMsg.replace('Monthly POI limit reached.', '').trim()
            if (msg.startsWith('Starter accounts include')) {
              const parts = msg.split(' Upgrade to Pro')
              return (
                <>
                  {parts[0]}
                  <br />
                  {parts.length > 1 ? `Upgrade to Pro${parts[1]}` : ''}
                </>
              )
            }
            return msg
          })()}
        </p>
        <p style={{
          fontFamily: 'Inter, system-ui, sans-serif',
          fontWeight: 400,
          fontSize: '12px',
          lineHeight: '1.5',
          color: '#6B7280',
          marginBottom: '16px',
          marginTop: '-8px'
        }}
        >
          Contact <a href={`${process.env.CORPORATE_URL}/contact/`} target='_blank' style={{ color: '#10B981', textDecoration: 'none' }} onMouseOver={(e) => { e.target.style.textDecoration = 'underline' }} onMouseOut={(e) => { e.target.style.textDecoration = 'none' }} rel='noreferrer'>customer support</a> for additional options
        </p>
        <button
          style={{ ...buttonStyle, backgroundColor: '#64cf5a' }}
          onClick={handleUpgradeClick}
          onMouseOver={(e) => { e.currentTarget.style.backgroundColor = '#5fc556' }}
        >
          Upgrade to Pro
        </button>
      </div>
    </div>
  )
}

const EnhancedUpgradeToProCard = compose(
  withState('isVisible', 'setIsVisible', true),
  lifecycle({
    componentDidUpdate: function (prevProps) {
      const limitJustExceeded = !prevProps.isLimitExceeded && this.props.isLimitExceeded
      const isRetrying = prevProps.isLoading && !this.props.isLoading && this.props.isLimitExceeded

      if (limitJustExceeded || isRetrying) {
        this.props.setIsVisible(true)
      }
    }
  })
)(UpgradeToProCardView)

const UpgradeToProCardConnector = inject('uiStore', 'dataStore')(observer(({ uiStore, dataStore }) => {
  const isLimitExceeded = uiStore.isLimitExceeded || (dataStore && dataStore.poi && dataStore.poi.isLimitExceeded)
  const limitMessage = uiStore.limitExceededMessage || (dataStore && dataStore.poi && dataStore.poi.limitExceededMessage)
  const isExpanded = uiStore.isPOIMenuExpanded
  const isLoading = uiStore.loading || (dataStore && dataStore.poi && dataStore.poi.loading)

  return (
    <EnhancedUpgradeToProCard
      isLimitExceeded={isLimitExceeded}
      limitMessage={limitMessage}
      isExpanded={isExpanded}
      isLoading={isLoading}
      uiStore={uiStore}
    />
  )
}))

export default UpgradeToProCardConnector
